import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { paymentsAPI } from '../utils/api';
import { format } from 'date-fns';
import { FiSearch, FiCreditCard, FiDownload } from 'react-icons/fi';
import './Payments.css';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [method, setMethod] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchPayments();
  }, [pagination.page, method]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await paymentsAPI.getAll({
        search,
        method,
        page: pagination.page,
        limit: pagination.limit,
      });
      setPayments(response.data.payments);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      }));
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchPayments();
  };

  const formatCurrency = (amount, currency = 'AED') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const getMethodBadge = (method) => {
    const methodClasses = {
      cash: 'badge-paid',
      bank_transfer: 'badge-sent',
      check: 'badge-viewed',
      credit_card: 'badge-partial',
      stripe: 'badge-sent',
      paypal: 'badge-sent',
      other: 'badge-draft',
    };
    return methodClasses[method] || 'badge-draft';
  };

  const exportToCSV = () => {
    const headers = ['Payment #', 'Invoice', 'Client', 'Amount', 'Method', 'Date', 'Status'];
    const rows = payments.map(p => [
      p.payment_number,
      p.invoice_number,
      p.company_name,
      formatCurrency(p.amount, p.currency),
      p.payment_method,
      formatDate(p.payment_date),
      p.status,
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `payments-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Payments</h1>
          <p>View and manage all payments</p>
        </div>
        <button className="btn btn-secondary" onClick={exportToCSV}>
          <FiDownload /> Export CSV
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSearch} className="search-bar">
            <div className="search-input">
              <FiSearch className="search-input-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Search payments..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="form-control"
              style={{ width: 'auto' }}
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="">All Methods</option>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="check">Check</option>
              <option value="credit_card">Credit Card</option>
              <option value="stripe">Stripe</option>
              <option value="paypal">PayPal</option>
              <option value="other">Other</option>
            </select>
            <button type="submit" className="btn btn-secondary">
              Search
            </button>
          </form>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : payments.length === 0 ? (
            <div className="empty-state">
              <FiCreditCard className="empty-state-icon" />
              <h3 className="empty-state-title">No payments found</h3>
              <p className="empty-state-text">
                {search || method ? 'Try adjusting your search criteria' : 'Payments will appear here when recorded'}
              </p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Payment #</th>
                      <th>Invoice</th>
                      <th>Client</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="font-medium">{payment.payment_number}</td>
                        <td>
                          <a href={`/invoices/${payment.invoice_id}`} className="text-primary">
                            {payment.invoice_number}
                          </a>
                        </td>
                        <td>{payment.company_name}</td>
                        <td className="font-medium">{formatCurrency(payment.amount, payment.currency)}</td>
                        <td>
                          <span className={`badge ${getMethodBadge(payment.payment_method)}`}>
                            {payment.payment_method.replace('_', ' ')}
                          </span>
                        </td>
                        <td>{formatDate(payment.payment_date)}</td>
                        <td>
                          <span className={`badge ${payment.status === 'completed' ? 'badge-paid' : 'badge-cancelled'}`}>
                            {payment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Payments;